import { useMemo } from 'react';
import { calculateTradeValues, TradeEntryFormData } from '@/services/trades/trade-service';
import type { PositionDetail, TradeType } from '@/types';
import { reRateMode } from '@/services/trades/rerate-mapper';
import { effectiveRates } from '@/services/trades/effective-rate';

const num = (v: unknown): number | undefined => {
  if (v === null || v === undefined || v === '') return undefined;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : undefined;
};

export interface ReRateInputs {
  /** rebate mode — the benchmark rate % (undefined leaves the booked value). */
  benchmarkRate?: number;
  /** rebate mode — the spread in bps (signed; LR-464 allows negatives). */
  spreadBps?: number;
  /** fee mode — the fee %. */
  feePercent?: number;
}

export interface ReRatePreview {
  mode: 'rebate' | 'fee';
  /** Headline rate BEFORE — total_rate (rebate) or fee_percent (fee), per KpiStrip. */
  currentRate: number | undefined;
  /** Headline rate AFTER, same convention. */
  newRate: number;
  /** Current daily PnL — the authoritative backend figure (security ccy STRING). */
  currentDaily: string | null;
  currentDailyConverted: string | null;
  /** Projected daily PnL AFTER — the backend current PLUS the rate-effect delta. */
  newDaily: number;
  /** Cross-ccy settlement-currency twin of newDaily (undefined same-ccy). */
  newDailyConverted: number | undefined;
  /** The rate-effect delta only (calc(new) − calc(current)), same-basis so it is 0
   *  when nothing moved — security ccy. */
  deltaDaily: number;
  /** Settlement-ccy twin of deltaDaily (undefined same-ccy). */
  deltaDailyConverted: number | undefined;
  isCrossCurrency: boolean;
}

/**
 * LR-528 re-rate preview. The CURRENT daily PnL shown is the backend's
 * authoritative `trade.daily_earnings` (the same source KpiStrip / PnlTable read).
 * The PROJECTED daily is that authoritative figure PLUS a rate-effect delta — the
 * delta is `calculateTradeValues(newRate) − calculateTradeValues(currentRate)` on
 * the SAME booked basis (marked CV), so it isolates the rate change and is exactly
 * 0 when nothing moved. Anchoring "after" to the real "before" (rather than showing
 * the raw calc) keeps the two figures on one scale — the backend CV/sign basis can
 * differ from a from-scratch FE recompute, which would otherwise make before→after
 * look nonsensical. Only `expectedReturn` is date-independent, so no dates needed.
 */
export function useReRatePreview(
  trade: PositionDetail,
  { benchmarkRate, spreadBps, feePercent }: ReRateInputs,
): ReRatePreview {
  return useMemo(() => {
    const mode = reRateMode(trade);
    const securityCurrency = trade.currency || 'USD';
    const settlementCurrency = trade.settlement_currency || securityCurrency;
    const isCrossCurrency = settlementCurrency !== securityCurrency;
    // CV basis: prefer the live mark so the delta is on the same basis the backend
    // prices on. Falls back to gross_price for a not-yet-marked position.
    const contractPrice = num(trade.latest_mark_price) ?? num(trade.gross_price) ?? 0;

    // Daily PnL for a given rate leg, via the amend-preview math.
    const dailyFor = (benchmark: number, spread: number, fee: number) => {
      let total: number;
      if (mode === 'fee') total = fee - benchmark;
      else if (trade.rate_type === 'fixed') total = benchmark;
      else total = benchmark + spread / 100;
      const calc = calculateTradeValues({
        security: { currency: securityCurrency },
        position: {
          quantity: num(trade.quantity) ?? 0,
          contractPrice,
          marginBps: num(trade.margin_bps) ?? 0,
          availableQuantity: num(trade.available_quantity) ?? num(trade.quantity) ?? 0,
          tradeType: trade.trade_type as TradeType,
        },
        rate: {
          // fee mode ignores fixedRate (nets fee_income − cost_of_collateral);
          // rebate reads the resolved total. benchmarkRate feeds fee cost_of_collateral.
          fixedRate: mode === 'fee' ? 0 : total,
          rateEntryMode: mode,
          benchmarkRate: benchmark,
          spreadBps: spread,
          feePercent: fee,
          dayCountConvention: trade.day_count_convention ?? undefined,
          poolAccruesInterest: trade.pool_accrues_interest,
        },
        settlement: { settlementCurrency, fxRate: num(trade.fx_rate) },
      } as Partial<TradeEntryFormData>);
      return { total, daily: calc.expectedReturn, converted: calc.expectedDailyReturnConverted };
    };

    // LR-628: on a floating rebate the "before" leg has to sit on the benchmark
    // the trade is ACTUALLY accruing at today, not the booked snapshot —
    // otherwise the delta silently folds a market move into what looks like the
    // effect of the trader's own spread change.
    const { benchmarkRate: liveBenchmark, totalRate: liveTotalRate, dailyEarnings, dailyEarningsConverted } =
      effectiveRates(trade);
    const bookedBenchmark = num(liveBenchmark) ?? 0;
    const bookedSpread = num(trade.spread_bps) ?? 0;
    const bookedFee = num(trade.fee_percent) ?? 0;
    const cur = dailyFor(bookedBenchmark, bookedSpread, bookedFee);
    const nxt = dailyFor(
      num(benchmarkRate) ?? bookedBenchmark,
      num(spreadBps) ?? bookedSpread,
      num(feePercent) ?? bookedFee,
    );

    const deltaDaily = nxt.daily - cur.daily;
    const deltaDailyConverted =
      nxt.converted !== undefined && cur.converted !== undefined ? nxt.converted - cur.converted : undefined;

    // Anchor "after" to the authoritative "before" + the rate-effect delta.
    const realDaily = num(dailyEarnings);
    const realDailyConverted = num(dailyEarningsConverted);
    const newDaily = (realDaily ?? cur.daily) + deltaDaily;
    const newDailyConverted =
      deltaDailyConverted === undefined
        ? undefined
        : (realDailyConverted ?? cur.converted ?? 0) + deltaDailyConverted;

    return {
      mode,
      currentRate: mode === 'fee' ? num(trade.fee_percent) : num(liveTotalRate),
      newRate: mode === 'fee' ? (num(feePercent) ?? bookedFee) : nxt.total,
      currentDaily: dailyEarnings ?? null,
      currentDailyConverted: dailyEarningsConverted ?? null,
      newDaily,
      newDailyConverted,
      deltaDaily,
      deltaDailyConverted,
      isCrossCurrency,
    };
  }, [trade, benchmarkRate, spreadBps, feePercent]);
}
